import type { CommandRegistry } from "./registry.ts";
import type { Command, PaneRouter } from "./types.ts";
import type { Profile } from "../config/profile.ts";
import {
  applyTheme,
  getTheme,
  loadSavedThemeId,
  preferredTheme,
  saveTheme,
  themes,
} from "../config/themes.ts";

const PROMPT_SUFFIX = ":~$";

/** Below this width the terminal fills the screen and dragging is disabled.
    Must match the @media breakpoint in styles.css. */
const COMPACT_BP = 900;

/** Commands offered as tap targets on compact layouts. */
const CHIP_COMMANDS = ["/help", "/about", "/projects", "/blog", "/theme"];

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export class Terminal {
  readonly registry: CommandRegistry;
  readonly profile: Profile;

  /** Set by main.ts once the pane and router exist. Commands reach the
      pane through this; undefined only if the app booted without one. */
  router?: PaneRouter;

  private root!: HTMLElement; // the floating window
  private bar!: HTMLElement; // draggable title bar
  private out!: HTMLElement; // scrollback
  private inputLine!: HTMLElement;
  private input!: HTMLInputElement;
  private promptEl!: HTMLElement;
  private mirror!: HTMLElement; // measures typed-text width for the block cursor
  private caret!: HTMLElement; // the blinking block cursor
  private charW = 9; // measured character cell width in px

  private history: string[] = [];
  private histIndex = 0; // points one past the last entry

  private chips!: HTMLElement;

  private moved = false; // has the user dragged the window?
  private docked = false; // is a pane open beside us?
  private reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
  private compactQuery = matchMedia(`(max-width: ${COMPACT_BP}px)`);

  constructor(mount: HTMLElement, registry: CommandRegistry, profile: Profile) {
    this.registry = registry;
    this.profile = profile;
    this.build(mount);
    this.applyInitialTheme();
    this.trackViewport();
    this.applyLayout();

    addEventListener("resize", () => this.applyLayout());
    this.compactQuery.addEventListener("change", () => {
      this.moved = false; // a stale drag offset makes no sense in a new layout
      this.applyLayout();
    });
  }

  /** True when the viewport is too narrow to float or tile windows. */
  private get compact(): boolean {
    return this.compactQuery.matches;
  }

  /* ── Viewport ─────────────────────────────────────────────
     On mobile the on-screen keyboard shrinks the *visual* viewport but
     leaves the layout viewport (and therefore `position: fixed` elements)
     untouched — so the input line ends up hidden behind the keyboard.
     Mirroring visualViewport.height into --vvh keeps everything visible. */

  private trackViewport(): void {
    const vv = window.visualViewport;
    const sync = () => {
      const h = vv ? vv.height : innerHeight;
      document.documentElement.style.setProperty("--vvh", `${Math.round(h)}px`);
    };
    sync();
    vv?.addEventListener("resize", sync);
    vv?.addEventListener("scroll", sync);
    addEventListener("orientationchange", sync);
  }

  /** Position the window for the current layout. Floating windows are placed
      with inline left/top; docked and compact layouts are placed by CSS, so
      the inline values must be cleared or they would win. */
  private applyLayout(): void {
    if (this.compact || this.docked) {
      this.root.style.left = "";
      this.root.style.top = "";
      return;
    }
    if (!this.moved) this.center();
  }

  /** Dock into a column beside an open pane (or undock and float again). */
  setDocked(docked: boolean): void {
    this.docked = docked;
    this.root.classList.toggle("docked", docked);
    this.applyLayout();
  }

  /* ── DOM ──────────────────────────────────────────────── */

  private build(mount: HTMLElement): void {
    const p = this.profile;
    this.root = el("section", "terminal");
    this.root.setAttribute("role", "application");
    this.root.setAttribute("aria-label", "Interactive portfolio terminal");

    this.bar = el("header", "titlebar");
    this.bar.innerHTML = `
      <div class="title">${esc(p.handle)}@${esc(p.host)} — bash</div>
      <div class="bar-tag" aria-hidden="true">drag me</div>`;

    this.out = el("div", "output");
    this.out.setAttribute("aria-live", "polite");

    this.inputLine = el("div", "inputline");
    this.promptEl = el("span", "prompt");
    this.promptEl.innerHTML = this.promptHTML();

    const field = el("span", "field");
    this.input = document.createElement("input");
    this.input.className = "cmdline";
    this.input.type = "text";
    this.input.autocomplete = "off";
    this.input.spellcheck = false;
    this.input.setAttribute("aria-label", "Terminal input");
    this.input.setAttribute("autocapitalize", "off");
    this.mirror = el("span", "mirror");
    this.mirror.setAttribute("aria-hidden", "true");
    this.caret = el("span", "caret");
    this.caret.setAttribute("aria-hidden", "true");
    field.append(this.input, this.mirror, this.caret);
    this.inputLine.append(this.promptEl, field);

    // Tap targets for touch users — typing "/projects" on a phone keyboard is
    // the real barrier to a text-only UI. Hidden by CSS on wide screens.
    this.chips = el("div", "chips");
    this.chips.setAttribute("aria-label", "Quick commands");
    for (const cmd of CHIP_COMMANDS) {
      const b = document.createElement("button");
      b.className = "chip";
      b.type = "button";
      b.textContent = cmd;
      b.addEventListener("click", () => {
        this.input.value = "";
        this.runLine(cmd);
      });
      this.chips.append(b);
    }

    this.root.append(this.bar, this.out, this.inputLine, this.chips);
    mount.append(this.root);

    this.wireInput();
    // Dragging is meaningless when the window fills the screen, and
    // `touch-action: none` on the title bar would block scrolling for nothing.
    if (!this.compact) this.wireDrag();
    this.wireFocus();
    this.wireCursor();
    this.wireOutputLinks();
  }

  /** Links printed into the scrollback are clickable:
        [data-post] opens the pane, [data-run] runs a command.
      Only on a plain left-click, so ctrl/cmd-click and "open in new tab"
      keep working on the real href. */
  private wireOutputLinks(): void {
    this.out.addEventListener("click", (e) => {
      const a = (e.target as HTMLElement).closest<HTMLAnchorElement>(
        "a[data-post], a[data-run]",
      );
      if (!a) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      if (a.dataset.post) {
        void this.router?.openPost(a.dataset.post);
      } else if (a.dataset.run) {
        this.input.value = "";
        this.runLine(a.dataset.run);
      }
    });
  }

  private promptHTML(): string {
    const p = this.profile;
    return `<span class="p-user">${esc(p.handle)}</span><span class="p-at">@</span><span class="p-host">${esc(
      p.host,
    )}</span><span class="p-path">${PROMPT_SUFFIX}</span>`;
  }

  /* ── Block cursor ─────────────────────────────────────── */

  private wireCursor(): void {
    const upd = () => this.updateCursor();
    this.input.addEventListener("input", upd);
    this.input.addEventListener("keyup", upd);
    this.input.addEventListener("click", upd);
    this.input.addEventListener("focus", upd);
    this.input.addEventListener("blur", upd);
    this.input.addEventListener("scroll", upd);
    addEventListener("resize", () => {
      this.measureCharW();
      upd();
    });
    // Web fonts load async and change glyph widths — recompute when ready.
    document.fonts?.ready.then(() => {
      this.measureCharW();
      upd();
    });
    this.measureCharW();
    upd();
  }

  private measureCharW(): void {
    const prev = this.mirror.textContent;
    this.mirror.textContent = "M";
    const w = this.mirror.offsetWidth;
    this.mirror.textContent = prev ?? "";
    if (w > 0) {
      this.charW = w;
      this.caret.style.width = w + "px";
    }
  }

  /** Position the block cursor after the typed text, or fall back to the
      native caret when editing mid-string. */
  private updateCursor(): void {
    this.mirror.textContent = this.input.value;
    const textW = this.mirror.offsetWidth;
    const focused = document.activeElement === this.input;
    const atEnd =
      this.input.selectionStart === this.input.value.length &&
      this.input.selectionStart === this.input.selectionEnd;

    if (focused && atEnd) {
      this.caret.style.left = textW - this.input.scrollLeft + "px";
      this.caret.style.display = "";
      this.input.style.caretColor = "transparent";
    } else {
      this.caret.style.display = "none";
      this.input.style.caretColor = "";
    }
  }

  /* ── Output ───────────────────────────────────────────── */

  print(text = "", cls = ""): void {
    const line = el("div", "line" + (cls ? " " + cls : ""));
    line.textContent = text;
    this.out.append(line);
    this.scroll();
  }

  printHTML(html: string): void {
    const line = el("div", "line");
    line.innerHTML = html;
    this.out.append(line);
    this.scroll();
  }

  clear(): void {
    this.out.replaceChildren();
  }

  private scroll(): void {
    this.out.scrollTop = this.out.scrollHeight;
  }

  /* ── Input handling ───────────────────────────────────── */

  private wireInput(): void {
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        const v = this.input.value;
        this.input.value = "";
        this.runLine(v);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.historyPrev();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        this.historyNext();
      } else if (e.key === "Tab") {
        e.preventDefault();
        this.complete();
      } else if (e.key === "l" && e.ctrlKey) {
        e.preventDefault();
        this.clear();
      } else if (e.key === "c" && e.ctrlKey) {
        this.echoLine(this.input.value + "^C");
        this.input.value = "";
      }
      this.updateCursor();
    });
  }

  private echoLine(value: string): void {
    this.printHTML(`<span class="prompt">${this.promptHTML()}</span> <span class="echo">${esc(value)}</span>`);
  }

  private async runLine(raw: string): Promise<void> {
    const value = raw.trim();
    this.echoLine(raw);
    if (value) {
      this.history.push(value);
      this.histIndex = this.history.length;
      document.getElementById("hint")?.style.setProperty("opacity", "0");
    } else {
      return;
    }

    // Allow both "/help" and "help".
    const cleaned = value.startsWith("/") ? value.slice(1) : value;
    const parts = cleaned.split(/\s+/);
    const name = parts[0] ?? "";
    const args = parts.slice(1);

    const cmd = this.registry.get(name);
    if (!cmd) {
      this.printHTML(
        `<span class="c-warn">command not found:</span> ${esc(name)} — type <span class="c-accent">/help</span>`,
      );
      return;
    }
    try {
      await cmd.run({
        args,
        raw: value,
        term: this,
        print: (t, c) => this.print(t, c),
        printHTML: (h) => this.printHTML(h),
        clear: () => this.clear(),
      });
    } catch (err) {
      this.print(String(err), "c-warn");
    }
  }

  private historyPrev(): void {
    if (this.history.length === 0) return;
    this.histIndex = Math.max(0, this.histIndex - 1);
    this.input.value = this.history[this.histIndex] ?? "";
    this.moveCaretEnd();
  }

  private historyNext(): void {
    if (this.history.length === 0) return;
    this.histIndex = Math.min(this.history.length, this.histIndex + 1);
    this.input.value = this.history[this.histIndex] ?? "";
    this.moveCaretEnd();
  }

  private moveCaretEnd(): void {
    const len = this.input.value.length;
    requestAnimationFrame(() => {
      this.input.setSelectionRange(len, len);
      this.updateCursor();
    });
  }

  private complete(): void {
    const current = this.input.value.replace(/^\//, "");
    if (!current || /\s/.test(current)) return;
    const matches = [...new Set(this.registry.list().map((c) => c.name))].filter((n) =>
      n.startsWith(current),
    );
    if (matches.length === 1) {
      this.input.value = "/" + matches[0];
    } else if (matches.length > 1) {
      this.printHTML(matches.map((m) => `<span class="c-accent">${esc(m)}</span>`).join("&nbsp;&nbsp;"));
    }
    this.updateCursor();
  }

  /* ── Theme control (used by /theme) ───────────────────── */

  setTheme(id: string): boolean {
    const t = getTheme(id);
    if (!t) return false;
    applyTheme(t);
    saveTheme(id);
    return true;
  }

  listThemes() {
    return themes;
  }

  private applyInitialTheme(): void {
    // A saved choice wins; otherwise follow the OS light/dark preference
    // rather than imposing one.
    const saved = loadSavedThemeId();
    applyTheme((saved && getTheme(saved)) || preferredTheme());
  }

  /* ── Dragging ─────────────────────────────────────────── */

  private wireDrag(): void {
    let startX = 0,
      startY = 0,
      baseLeft = 0,
      baseTop = 0,
      dragging = false;

    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const w = this.root.offsetWidth;
      const h = this.root.offsetHeight;
      const left = clamp(baseLeft + dx, 8 - w * 0.6, innerWidth - w * 0.4);
      const top = clamp(baseTop + dy, 8, innerHeight - 48);
      this.root.style.left = left + "px";
      this.root.style.top = top + "px";
    };

    const onUp = (e: PointerEvent) => {
      dragging = false;
      this.bar.classList.remove("grabbing");
      this.bar.releasePointerCapture?.(e.pointerId);
      removeEventListener("pointermove", onMove);
      removeEventListener("pointerup", onUp);
    };

    this.bar.addEventListener("pointerdown", (e) => {
      dragging = true;
      this.moved = true;
      startX = e.clientX;
      startY = e.clientY;
      const r = this.root.getBoundingClientRect();
      baseLeft = r.left;
      baseTop = r.top;
      this.bar.classList.add("grabbing");
      this.bar.setPointerCapture?.(e.pointerId);
      addEventListener("pointermove", onMove);
      addEventListener("pointerup", onUp);
    });

    // Double-click the bar to re-center.
    this.bar.addEventListener("dblclick", () => {
      this.moved = false;
      this.center();
    });
  }

  private center(): void {
    requestAnimationFrame(() => {
      const w = this.root.offsetWidth;
      const h = this.root.offsetHeight;
      const left = Math.max(8, (innerWidth - w) / 2);
      const top = Math.max(12, (innerHeight - h) * 0.42);
      this.root.style.left = left + "px";
      this.root.style.top = top + "px";
    });
  }

  /* ── Focus ────────────────────────────────────────────── */

  private wireFocus(): void {
    this.root.addEventListener("mousedown", (e) => {
      const tgt = e.target as HTMLElement;
      if (tgt === this.input) return;
      if (tgt.closest(".titlebar")) return;
      if (tgt.closest(".chips")) return; // tapping a chip shouldn't open the keyboard
      // Let the user select text; only auto-focus if no selection is being made.
      const sel = getSelection();
      if (sel && sel.toString().length > 0) return;
      // defer so a click that ends a selection doesn't refocus mid-select
      setTimeout(() => this.focus(), 0);
    });
  }

  /** Focus the input. On compact layouts this is a no-op unless explicitly
      forced, because stealing focus pops the on-screen keyboard over the
      content the user just asked to see. */
  focus(force = false): void {
    if (this.compact && !force) return;
    this.input.focus();
    this.updateCursor();
  }

  /* ── Boot sequence ────────────────────────────────────── */

  async boot(banner: string[]): Promise<void> {
    if (this.reduceMotion) {
      banner.forEach((l) => this.printHTML(l));
      this.print("");
      this.runStartupHelpHint();
      this.focus();
      return;
    }
    for (const l of banner) {
      this.printHTML(l);
      await sleep(70);
    }
    this.print("");
    this.runStartupHelpHint();
    this.focus();
  }

  private runStartupHelpHint(): void {
    const run = (cmd: string) =>
      `<a class="link" href="#" data-run="${cmd}">${cmd}</a>`;
    this.printHTML(
      `<span class="c-muted">Type ${run("/help")} to list commands, or try ` +
        `${run("/about")} ${run("/projects")} ${run("/blog")}</span>`,
    );
    this.print("");
  }
}

/* ── helpers ──────────────────────────────────────────── */

function el(tag: string, cls: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = cls;
  return e;
}
function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}
function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
